import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCheckbox, IonInput, IonItem, IonLabel, IonNote } from '@ionic/react'
import classNames from 'classnames'
import { getDirMode } from 'mobile_web/modules/platform'
import strings from 'mobile_web/strings'
import React, { useState } from 'react'

import { getDirectApi } from 'mytours-read-common'
import Button from '../Button'
import Modal from '../modal'
import './styles.less'
import { Form, FormField } from 'mytours-read-api-typescript'

interface Props {
	backdropDismiss?: boolean
	showBackdrop?: boolean
	isOpen: boolean
	onDidDismiss?: (event: Event) => void
	form: Form
	skippable: boolean
	onFinish: () => void
}

const FormModal: React.FC<Props> = ({
	backdropDismiss = true,
	showBackdrop = true,
	isOpen,
	onDidDismiss,
	form,
	skippable,
	onFinish,
}): JSX.Element => {
	const [formInputs, setFormInputs] = useState<Record<string, string>>({})
	const [stqryRejectedInput, setStqryRejectedInput] = useState('')
	const [formErrors, setFormErrors] = useState<Record<string, string>>({})

	const updateFormInput = (field: FormField, value: string) => {
		setFormInputs({ ...formInputs, [field.slug]: String(value)})
		if (formErrors[field.slug]) {
			const newFormErrors = { ...formErrors }
			delete newFormErrors[field.slug]
			setFormErrors(newFormErrors)
		}
	}

	const validate = () => {
		const newFormErrors = { ...formErrors }
		form.fields.forEach(field => {
			if (!field.required) {
				return
			}

			if (field.kind === FormField.KindEnum.MarketingList) {
				if (formInputs[field.slug] !== 'true') {
					newFormErrors[field.slug] = skippable ? strings.form_marketing_list_skippable_empty : strings.form_marketing_list_empty
				}
			} else if (!formInputs[field.slug]) {
				newFormErrors[field.slug] = strings.form_field_empty
			}
		})
		setFormErrors(newFormErrors)
		if (Object.keys(newFormErrors).length === 0) {
			return true
		} else {
			return false
		}
	}

	const submit = async () => {
		if (!validate()) {
			return
		}

		const formData = new FormData()
		formData.append('stqry_rejected', stqryRejectedInput)
		form.fields.forEach(field => {
			let fieldValue = formInputs[field.slug]
			if (!fieldValue && field.kind === FormField.KindEnum.MarketingList) {
				fieldValue = 'false'
			}
			formData.append(field.slug, fieldValue)
		})
		try {
			await getDirectApi().formSubmit(form.id, { body: formData })
			onFinish()
		} catch (error) {
			if (error instanceof Response) {
				const errorMessage = await error.json()
				const newFormErrors = { ...formErrors }
				errorMessage.errors.forEach(({ field: slug, message }) => {
					if (form.fields.some(field => field.slug === slug)) {
						newFormErrors[slug] = message
					}
				})
				setFormErrors(newFormErrors)
			}
		}
	}

	return (
		<Modal
			data-cy="form-modal"
			isOpen={isOpen}
			showBackdrop={showBackdrop}
			backdropDismiss={backdropDismiss}
			cssClass="form-select-modal"
			onDidDismiss={onDidDismiss}
		>
			<IonCard dir={getDirMode()}>
				<IonCardHeader>
					<div className="header">
						<IonCardTitle>{form.title}</IonCardTitle>
						{form.instructions ? (
							<IonCardSubtitle className="body-text" dangerouslySetInnerHTML={{ __html: `${form.instructions}` }} />
						) : null}
					</div>
				</IonCardHeader>
				<IonCardContent>
					{/* To help detect spam: automated one might fill this field and get rejected by backend */}
					<div className="reject-field">
						<IonItem>
							<IonLabel>{'Label'}</IonLabel>
							<IonInput value={stqryRejectedInput} onIonChange={e => setStqryRejectedInput(String(e.target.value))} />
						</IonItem>
					</div>
					{form.fields.map(field => {
						const error = formErrors[field.slug]
						switch (field.kind) {
							case FormField.KindEnum.Email:
							case FormField.KindEnum.Text:
							case FormField.KindEnum.GivenName:
							case FormField.KindEnum.FamilyName:
								return (
									<>
										<IonLabel>{field.label}</IonLabel>
										<IonItem fill="outline" key={field.id} className={error ? 'ion-invalid' : undefined}>
											<IonInput value={formInputs[field.slug]} onIonChange={e => updateFormInput(field, String(e.target.value))} />
											<IonNote slot="error">{error}</IonNote>
										</IonItem>
									</>
								)
							case FormField.KindEnum.MarketingList:
								return (
									<IonItem key={field.id} className={classNames('checkbox-field', error && 'ion-invalid item-interactive')}>
										<IonCheckbox checked={formInputs[field.slug] === 'true'} onIonChange={e => updateFormInput(field, String(e.target.checked))} />
										<IonLabel className="ion-text-wrap">{field.label}</IonLabel>
										<IonNote slot="error">{error}</IonNote>
									</IonItem>
								)
							default:
								return null
						}
					})}

					<div className="buttons-container">
						{skippable ? (
							<Button fill="clear" onClick={onFinish} ariaLabel={strings.form_skip} className="btn clear">{strings.form_skip}</Button>
						) : null}

						<Button fill="clear" onClick={submit} ariaLabel={strings.submit} className="btn">{strings.submit}</Button>
					</div>
				</IonCardContent>
			</IonCard>
		</Modal>
	)
}

export default FormModal
